import { InlineLoading, InlineNotification } from '@carbon/react';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback } from 'react';
import {
  activeTractIdLoadableAtom,
  crmTractFormAtom,
  crmTractFormLoadableAtom,
  entitySearchAtom,
  selectedEntityLoadableAtom,
} from './atoms';
import {
  EntityAccountPicker,
  EntityAccountSelection,
} from './entity-account-picker';

interface EntitySearchWithAccountsProps {
  accountType: Array<{ value: string; label: string }>;
  accountSource: Array<{ value: string; label: string }>;
}

type EntitySearchForm = Awaited<ReturnType<typeof crmTractFormAtom.read>>;

/**
 * The tract wizard's entity step: the shared picker bound to the wizard's form
 * (it writes the chosen CRM account) and to the tract being edited (it shows the
 * account already assigned).
 * @param {EntitySearchWithAccountsProps} props - Options plus the resolved form
 * @return {JSX.Element} The picker
 */
const WizardEntityAccountPicker = ({
  form,
  ...props
}: EntitySearchWithAccountsProps & { form: EntitySearchForm }) => {
  const [, setCrmAccountId] = useAtom(form.crmAccountId);
  const onChange = useCallback(
    ({ accountId }: EntityAccountSelection) => setCrmAccountId(accountId),
    [setCrmAccountId]
  );

  const activeTractIdLoadable = useAtomValue(activeTractIdLoadableAtom);
  const hasActiveTract =
    activeTractIdLoadable.state === 'hasData' && !!activeTractIdLoadable.data;
  const selectedEntityLoadable = useAtomValue(selectedEntityLoadableAtom);
  const selected =
    hasActiveTract && selectedEntityLoadable.state === 'hasData'
      ? selectedEntityLoadable.data
      : null;

  return (
    <EntityAccountPicker
      {...props}
      searchAtom={entitySearchAtom}
      onChange={onChange}
      assignedAccount={selected?.account}
      assignedEntities={selected?.entities}
    />
  );
};

/**
 * Loadable gate for the wizard's entity step. The async `crmTractFormAtom`
 * suspends whenever it is read raw and re-derives (e.g. on mount of this step),
 * and the wizard route has no <Suspense> boundary above it. Gating on the
 * loadable here and passing the resolved form DOWN as a prop (mirrors
 * TractCreation) means the content never reads the async atom, so it can't
 * suspend.
 * @param {EntitySearchWithAccountsProps} props - Account type and source options
 * @return {JSX.Element} Loading/error state or the entity search content
 */
const EntitySearchWithAccounts = (props: EntitySearchWithAccountsProps) => {
  const formLoadable = useAtomValue(crmTractFormLoadableAtom);

  if (formLoadable.state === 'loading') {
    return <InlineLoading description="Loading tract form..." />;
  }

  if (formLoadable.state === 'hasError') {
    return (
      <InlineNotification
        kind="error"
        title="Error"
        subtitle="Failed to load tract form"
        lowContrast
      />
    );
  }

  if (formLoadable.state !== 'hasData' || !formLoadable.data) {
    return null;
  }

  return <WizardEntityAccountPicker {...props} form={formLoadable.data} />;
};

export { EntitySearchWithAccounts };
